import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { createTask } from "../redux/actions/taskActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { fetchFiles } from "../redux/actions/fileActions";
import CustomAutocomplete from "../components/CustomAutocomplete";
import FileAutocomplete from "../components/FileAutocomplete";
import { textFieldStyles } from "../styles/textFieldStyles";
import BreadcrumbNav from "../components/BreadcrumbNav";
import CancelButton from "../components/CancelButton";
import SubmitButton from "../components/SubmitButton";
import CustomDatePicker from "../components/CustomDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import enGB from "date-fns/esm/locale/en-GB";
import { nl } from "date-fns/locale";
import {
  Container,
  Autocomplete,
  TextField,
  Button,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Breadcrumbs,
  Link,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Snackbar, Alert } from "@mui/material";

const breadcrumbs = [
  { label: "Home", href: "/" },
  { label: "Tasks", href: "/tasks" },
];

const locales = {
  "en-GB": enGB,
  "nl": nl,
};

const buttonStyles = {
  borderRadius: 1, // Rounded corners
  padding: "6px 16px", // Custom padding
  textTransform: "none", // Disable uppercase text
  boxShadow: "none", // Remove default shadow
  "&:hover": {
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)", // Add custom shadow on hover
  },
};



const CreateTaskPage = () => {
  const { t, i18n } = useTranslation(['pages', 'forms', 'buttons', 'common']);

  const taskPriorities = [
    { value: "Low", label: t('pages:tasks.priority_options.low') },
    { value: "Medium", label: t('pages:tasks.priority_options.medium') },
    { value: "High", label: t('pages:tasks.priority_options.high') }
  ];

  const taskTypes = [
    { value: "Synchronization with Outlook", label: t('pages:tasks.types.synchronization_outlook') },
    { value: "Task in the procedure", label: t('pages:tasks.types.task_procedure') },
    { value: "Other task", label: t('pages:tasks.types.other_task') },
    { value: "Task directed towards the client", label: t('pages:tasks.types.task_client') },
    { value: "Phone note (lawyer) opposing party", label: t('pages:tasks.types.phone_note_lawyer_opposing') },
    { value: "Phone note client", label: t('pages:tasks.types.phone_note_client') },
    { value: "Phone note third party", label: t('pages:tasks.types.phone_note_third_party') },
  ];

  const taskStatuses = [
    { value: "Not started", label: t('pages:tasks.status_options.not_started') },
    { value: "In progress", label: t('pages:tasks.status_options.in_progress') },
    { value: "Completed", label: t('pages:tasks.status_options.completed') },
    { value: "Pending", label: t('pages:tasks.status_options.pending') },
    { value: "Deferred", label: t('pages:tasks.status_options.deferred') },
  ];

  const taskColours = [
    { value: "Red", name: t('common:colors.red'), color: "#FF0000" },
    { value: "Yellow", name: t('common:colors.yellow'), color: "#FFFF00" },
    { value: "Green", name: t('common:colors.green'), color: "#008000" },
    { value: "Blue", name: t('common:colors.blue'), color: "#0000FF" },
    { value: "No colour", name: t('common:colors.no_color'), color: "#FFFFFF" },
  ];

  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: t('navigation:home'), href: "/" },
    { label: t('pages:tasks.title'), href: "/tasks" },
  ]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { relationships } = useSelector((state) => state.relationshipList);
  const { files } = useSelector((state) => state.fileList);
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state
  const { loading, success, error } = useSelector((state) => state.taskCreate);
  // const [owner, setOwner] = useState(location.state?.owner?._id || "");
  const [owner, setOwner] = useState("");
  const [assignee, setAssignee] = useState("");
  const [type, setType] = useState("");
  
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [reminder, setReminder] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("Low");
  const [colour, setColour] = useState("No colour");
  const [userRole, setUserRole] = useState(null);
  const [userRelationship, setUserRelationship] = useState(null);
  // 2. Add state inside component
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (user) {
      setUserRole(user.role);
      setOwner(user.relationship);
    }
  }, [user]);

  useEffect(() => {
    dispatch(fetchRelationships());
    dispatch(fetchFiles());
  }, [dispatch]);
  // Filter files based on user role
  const filteredFiles = files.filter((file) => {
    if (user.role === "Manager") {
      return true; // Managers can see all files
    } else if (user.role === "Responsible Lawyer") {
      return file.officeMembers.some((member) => {
        // console.log('Member ID:', member.member._id);
        // console.log('User Relationship ID:', user.relationship);
        return (
          member.member._id === user.relationship &&
          member.role === "Responsible Lawyer"
        );
      });
    } else if (user.role === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });
  
  const [caseId, setCaseId] = useState(() => {
    // Check location state first
    const locationCaseId = location.state?.caseId;

    const initialCaseFile = filteredFiles?.find((file) => file._id === locationCaseId) || null;

    // Update breadcrumbs if initialCaseFile exists
    if (initialCaseFile) {
      setBreadcrumbs((prevBreadcrumbs) => [
        ...prevBreadcrumbs,
        {
          label: initialCaseFile.name || "Case File",
          href: `/files/${initialCaseFile._id}/tasks`,
        },
      ]);
    }

    if (locationCaseId) return locationCaseId;

    
    // Then check localStorage
    const storedCaseId = localStorage.getItem("selectedCaseId");
    if (storedCaseId) return storedCaseId;

    // Default to empty string if neither exists
    return "";
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const newTask = {
      owner,
      ...(assignee && { assignee }),
      type,
      case: caseId,
      subject,
      description,
      dueDate, // This will still be in YYYY-MM-DD format as needed by the API
      reminder,
      status,
      priority,
      colour,
    };
    dispatch(createTask(newTask));
    // navigate("/tasks");
  };

  const handleCancel = () => {
    navigate("/tasks");
  };

  // 3. Add useEffect to watch task creation status
  useEffect(() => {
    if (success) {
      setNotification({
        open: true,
        message: t('pages:tasks.created_successfully'),
        severity: "success",
      });
      dispatch({ type: "TASK_CREATE_RESET" }); // Reset the success state
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    
    } else if (error) {
      setNotification({
        open: true,
        message: error,
        severity: "error",
      });
      dispatch({ type: "TASK_CREATE_RESET" }); // Reset the error state
    }
  }, [success, error, dispatch, navigate]);

  // 4. Add close handler
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={t('pages:tasks.create_new')}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />

      <Box
        sx={{
          backgroundColor: "white",
          padding: "30px 24px",
          marginTop: "24px",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Row 1: Owner | Assigned To | Type */}
            <Grid item xs={12} md={4}>
              <FormControl variant="standard" sx={{ width: "100%" }}>
                <CustomAutocomplete
                  options={relationships}
                  value={relationships?.find((rel) => rel._id === owner) || null}
                  onChange={(event, newValue) =>
                    setOwner(newValue ? newValue._id : "")
                  }
                  label={t('forms:labels.owner')}
                  filterType="Member"
                  addNewLabel="Add Owner"
                  addNewPath="/relationships/create"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              {(user?.role === "Manager") && (
                <FormControl variant="standard" sx={{ width: "100%" }}>
                  <CustomAutocomplete
                    options={relationships}
                    value={relationships?.find((rel) => rel._id === assignee) || null}
                    onChange={(event, newValue) =>
                      setAssignee(newValue ? newValue._id : "")
                    }
                    label={t('forms:labels.assignedTo')}
                    filterType="Member"
                    addNewLabel={t('common:add_owner')}
                    addNewPath="/relationships/create"
                  />
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={taskTypes}
                getOptionLabel={(option) => option.label}
                value={taskTypes.find(t => t.value === type) || null}
                size="small"
                onChange={(event, newValue) => setType(newValue ? newValue.value : "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('common:type')}
                    variant="outlined"
                    required
                  />
                )}
                sx={textFieldStyles({ width: "100%" })}
              />
            </Grid>

            {/* Row 2: Case | Subject (spans 2 columns) */}
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" sx={{ width: "100%" }}>
                <FileAutocomplete
                  options={filteredFiles}
                  value={
                    filteredFiles?.find((file) => file._id === caseId) || null
                  }
                  onChange={(event, newValue) =>
                    setCaseId(newValue ? newValue._id : "")
                  }
                  label={t('common:case')}
                  name="case"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label={t('forms:labels.subject')}
                variant="outlined"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                sx={textFieldStyles({ width: "100%" })}
                size="small"
              />
            </Grid>

            {/* Row 3: Status | Description (Description spans 2 columns) */}
            <Grid item xs={12} md={4}>
              <FormControl
                variant="outlined"
                sx={textFieldStyles({ width: "100%" })}
                size="small"
                required
              >
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select
                  labelId="status-select-label"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  label={t('forms:labels.status')}
                >
                  {taskStatuses.map((taskStatus) => (
                    <MenuItem key={taskStatus.value} value={taskStatus.value}>
                      {taskStatus.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label={t('forms:labels.description')}
                variant="outlined"
                rows={1}
                multiline
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={textFieldStyles({ width: "100%" })}
                size="small"
              />
            </Grid>

            {/* Row 4: Reminder days | Due Date | Priority */}
            <Grid item xs={12} md={4}>
              <TextField
                label={t('forms:placeholders.reminder_days_before')}
                variant="outlined"
                type="number"
                fullWidth
                value={reminder}
                onChange={(e) => setReminder(e.target.value)}
                sx={textFieldStyles({ width: "100%" })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={i18n.language === 'nl' ? nl : enGB}>
                <DateTimePicker
                  value={dueDate}
                  onChange={(newValue) => setDueDate(newValue)}
                  inputFormat="dd/MM/yyyy HH:mm"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      sx: textFieldStyles({ width: "100%" }),
                      size: "small",
                      label: t('forms:labels.dueDate'),
                    },
                  }}
                  minutesStep={15}
                  ampm={false}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl
                variant="outlined"
                sx={textFieldStyles({ width: "100%" })}
                size="small"
              >
                <InputLabel id="priority-select-label">{t('forms:labels.priority')}</InputLabel>
                <Select
                  labelId="priority-select-label"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  label={t('forms:labels.priority')}
                >
                  {taskPriorities.map((taskPriority) => (
                    <MenuItem key={taskPriority.value} value={taskPriority.value}>
                      {taskPriority.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Row 5: Colour */}
            <Grid item xs={12} md={4}>
              <FormControl
                variant="outlined"
                sx={textFieldStyles({ width: "100%" })}
                size="small"
              >
                <InputLabel id="colour-select-label">{t('forms:labels.colour')}</InputLabel>
                <Select
                  labelId="colour-select-label"
                  value={colour}
                  onChange={(e) => setColour(e.target.value)}
                  label={t('forms:labels.colour')}
                >
                  {taskColours.map((taskColour) => (
                    <MenuItem key={taskColour.value} value={taskColour.value}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            backgroundColor: taskColour.color,
                            marginRight: 1,
                          }}
                        />
                        {taskColour.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "24px",
              gap: 2,
            }}
          >
            <CancelButton onClick={handleCancel} />
            <SubmitButton
              type="submit"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? t('buttons:status.processing') : t('buttons:primary.create')}
            </SubmitButton>
          </Box>
        </form>
      </Box>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateTaskPage;
